<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import JobForm from './components/JobConfigurationForm.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import { JobConfigurationDto } from '@/api/generator';
import { useRouter } from 'vue-router';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { toCreateOrUpdateJobConfigurationDto } from './mapping';
import { NIL } from 'uuid';
import type { PartialDeep } from 'type-fest';
import { DateTime, Settings } from 'luxon';
import { useLoading } from '@/utils/useLoading';
import { computed, onMounted, provide } from 'vue';
import { isCopyInjectionKey } from './injectionKeys';
import { makeCopyText } from '@/utils/helper';
import { useTranslation } from 'i18next-vue';
import { useNotificationStore } from '@/stores/notificationStore';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator';
import { formBackendErrorHandler } from '@/errorHandling';

const { t } = useTranslation();
const notificationStore = useNotificationStore();

interface JobConfigurationAddViewProps {
  copyId?: string;
}

const props = defineProps<JobConfigurationAddViewProps>();

const router = useRouter();

provide(
  isCopyInjectionKey,
  computed(() => !!props.copyId),
);

const now = DateTime.now().set({ second: 0, millisecond: 0 });

const { action: loadJobConfiguration, loading } = useLoading(async () => {
  if (props.copyId) {
    const { result: copy } = await api.jobConfigurationsApi.getJobConfiguration({
      id: props.copyId,
    });

    if (copy) {
      resetForm({
        values: {
          ...copy,
          id: NIL,
          name: makeCopyText(copy.name),
          schedule: {
            ...copy.schedule,
            id: NIL,
          },
          tasks: copy.tasks?.map((task) => ({
            ...task,
            id: NIL,
            workSteps: task.workSteps?.map((step) => ({ ...step, id: NIL })),
          })),
        },
      });
    }
  }
}, true);

onMounted(loadJobConfiguration);

const initialValues: PartialDeep<JobConfigurationDto> = {
  id: NIL,
  isAvailable: true,
  name: t('Untitled'),
  schedule: {
    id: NIL,
    frequency: 'None',
    interval: 1,
    weekdays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    periodStart: now.toISODate(),
    periodEnd: now.plus({ weeks: 1 }).toISODate(),
    executionStart: now.toISO(),
    executionEnd: now.plus({ hours: 1 }).toISO(),
    timeZone: Settings.defaultZone.name,
  },
};

const { form } = useVeeForm<JobConfigurationDto>({
  options: {
    initialValues,
    keepValuesOnUnmount: true, // keep values when switching between tabs
  },
  schemaName: 'JobConfigurationDto',
  mode: 'add',
  customValidateFunctions: [
    backendValidator((jobConfigurationDto, errorHandlers) =>
      api.jobConfigurationsApi.validateJobConfiguration(
        {
          createOrUpdateJobConfigurationDto:
            toCreateOrUpdateJobConfigurationDto(jobConfigurationDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values, resetForm } = form;

const submit = handleSubmit(async (jobConfigurationDto) => {
  const createOrUpdateJobConfigurationDto =
    toCreateOrUpdateJobConfigurationDto(jobConfigurationDto);

  const { result, success } = await api.jobConfigurationsApi.createJobConfiguration(
    {
      createOrUpdateJobConfigurationDto,
    },
    [formBackendErrorHandler(form)],
  );

  if (success && result?.id) {
    notificationStore.success(t('EntityAdded', { name: result.name, iuid: result.iuid }));
    await router.push({
      name: routenames.jobConfigurations.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :title="values.name" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.jobConfigurations.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <JobForm />
    </BasePage>
  </form>
</template>
